# CVX Search API

Search CDC CVX codes for vaccines.

## Endpoint

```
GET /v1/cvx/search
```

## Quick Start

<LanguageBlock>
  <Block language="node">
    ```typescript
    import { Fhirfly } from "@fhirfly-io/terminology";

    const client = new Fhirfly({ apiKey: "YOUR_API_KEY" });

    // Search for COVID vaccines
    const results = await client.cvx.search({
      q: "covid"
    });

    console.log(`Found ${results.total} vaccines`);
    for (const item of results.items) {
      console.log(`${item.code}: ${item.display}`);
    }
    ```
  </Block>
  <Block language="curl">
    ```bash
    curl -H "x-api-key: YOUR_API_KEY" \
      "https://api.fhirfly.io/v1/cvx/search?q=covid"
    ```
  </Block>
</LanguageBlock>

## Parameters

### Text Search

| Parameter | Description | Example |
|-----------|-------------|---------|
| `q` | General text search | `q=influenza` |

### Filters

| Parameter | Type | Description | Example |
|-----------|------|-------------|---------|
| `status` | string | Vaccine status | `active`, `inactive` |
| `vaccine_type` | string | Vaccine type | `mRNA`, `viral vector` |
| `is_covid_vaccine` | boolean | COVID-19 vaccines only | `true` |

### Pagination & Response

| Parameter | Default | Max | Description |
|-----------|---------|-----|-------------|
| `limit` | 20 | 100 | Results per page |
| `page` | 1 | 100 | Page number |
| `shape` | compact | — | `compact`, `standard`, `full` |

## Example Response

```json
{
  "facets": {
    "is_covid_vaccine": {
      "true": 6
    },
    "status": {
      "active": 6
    },
    "vaccine_type": {
      "mRNA": 5
    }
  },
  "has_more": true,
  "items": [
    {
      "code": "334",
      "display": "COVID-19, mRNA, LNP-S, PF, 10 mcg/0.2 mL",
      "status": "active"
    },
    {
      "code": "311",
      "display": "COVID-19, mRNA, LNP-S, PF, 25 mcg/0.25 mL",
      "status": "active"
    }
  ],
  "limit": 2,
  "meta": {
    "legal": {
      "attribution_required": false,
      "citation": "CDC National Center for Immunization and Respiratory Diseases. Accessed 2026-01-28 via FHIRfly.",
      "license": "public_domain",
      "source_name": "CDC Immunization"
    }
  },
  "page": 1,
  "total": 6,
  "total_capped": false
}
```

## Use Cases

### Find All COVID-19 Vaccines

<LanguageBlock>
  <Block language="node">
    ```typescript
    const results = await client.cvx.search({
      is_covid_vaccine: true,
      status: "active"
    });
    ```
  </Block>
  <Block language="curl">
    ```bash
    curl -H "x-api-key: YOUR_API_KEY" \
      "https://api.fhirfly.io/v1/cvx/search?is_covid_vaccine=true&status=active"
    ```
  </Block>
</LanguageBlock>

### Find Flu Vaccines

<LanguageBlock>
  <Block language="node">
    ```typescript
    const results = await client.cvx.search({
      q: "influenza",
      status: "active"
    });
    ```
  </Block>
  <Block language="curl">
    ```bash
    curl -H "x-api-key: YOUR_API_KEY" \
      "https://api.fhirfly.io/v1/cvx/search?q=influenza&status=active"
    ```
  </Block>
</LanguageBlock>

### Find mRNA Vaccines

<LanguageBlock>
  <Block language="node">
    ```typescript
    const results = await client.cvx.search({
      vaccine_type: "mRNA"
    });
    ```
  </Block>
  <Block language="curl">
    ```bash
    curl -H "x-api-key: YOUR_API_KEY" \
      "https://api.fhirfly.io/v1/cvx/search?vaccine_type=mRNA"
    ```
  </Block>
</LanguageBlock>

### Find Childhood Vaccines

<LanguageBlock>
  <Block language="node">
    ```typescript
    const results = await client.cvx.search({
      q: "DTaP",
      status: "active"
    });
    ```
  </Block>
  <Block language="curl">
    ```bash
    curl -H "x-api-key: YOUR_API_KEY" \
      "https://api.fhirfly.io/v1/cvx/search?q=DTaP&status=active"
    ```
  </Block>
</LanguageBlock>

## Facets

CVX search returns these facets:

| Facet | Description |
|-------|-------------|
| `status` | Distribution by active/inactive |
| `vaccine_type` | Distribution by vaccine type |
| `is_covid_vaccine` | COVID vs non-COVID vaccines |

## Valid Values

### Status Values

- `active` — Currently available
- `inactive` — No longer in use

## Required Scope

`cvx.search`

## See Also

- [CVX Lookup API](/docs/value-apis/cvx) — Look up a specific CVX code
- [MVX Search API](/docs/search-apis/mvx) — Search vaccine manufacturers
- [Search APIs Overview](/docs/search-apis/overview) — Common search features
